import type { RevenueAnalyticsAction } from './actions';
import { EMPTY_REVENUE_ANALYTICS_COLUMN_FILTERS, REVENUE_ANALYTICS_FILTER_COLUMNS } from './types';
import type { RevenueAnalyticsState } from './types';
import {
    DEFAULT_AGGREGATED_SYMBOLS_COLUMN_VISIBILITY,
    DEFAULT_REVENUE_ANALYTICS_COLUMN_VISIBILITY,
    findAggregatedSymbolsColumn,
    findRevenueAnalyticsColumn,
    isEveryAggregatedSymbolsColumnVisible,
    isEveryRevenueAnalyticsColumnVisible,
} from 'src/admin/LAB/RevenueAnalytics/columns';

// Order-insensitive, like the query key: reordering the same providers is not a new slice.
const sameIds = (a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean => {
    if (a.length !== b.length) return false;
    const sortedB = [...b].sort();
    return [...a].sort().every((id, i) => id === sortedB[i]);
};

/**
 * Pure transitions for the Revenue Analytics view state. Selection,
 * page size, sort, filter, and search changes all snap the page back to
 * 1 so a `$skip` never outruns a freshly narrowed result set.
 */
export function revenueAnalyticsReducer(
    state: RevenueAnalyticsState,
    action: RevenueAnalyticsAction,
): RevenueAnalyticsState {
    switch (action.type) {
        case 'SET_FIRM': {
            // Only ever dispatched on a fresh instance (the model enforces
            // one-shot scoping), so this is a plain initialization.
            if (state.firmId === action.firmId) return state;
            return { ...state, firmId: action.firmId };
        }
        case 'SET_ACTIVE_TAB': {
            if (state.activeTab === action.tab) return state;
            // Each tab pages its own result set, and a provider sort has
            // no field on the symbol rows: paging and sort start fresh. The
            // column filters and symbol search carry over — they address the
            // same fields on both groupings.
            return { ...state, activeTab: action.tab, page: 1, sort: null };
        }
        case 'SELECT_PROVIDERS': {
            if (sameIds(state.selectedProviderIds, action.providerIds)) return state;
            // A new provider selection starts fresh: reset paging and clear any search.
            return {
                ...state,
                selectedProviderIds: [...action.providerIds],
                page: 1,
                symbolSearchInput: '',
                appliedSymbolSearch: '',
            };
        }
        case 'SET_PAGE':
            return { ...state, page: Math.max(1, Math.trunc(action.page)) };
        case 'SET_ROWS_PER_PAGE':
            if (state.rowsPerPage === action.rowsPerPage) return state;
            return { ...state, rowsPerPage: action.rowsPerPage, page: 1 };
        case 'TOGGLE_SORT': {
            // Cycle asc → desc → backend default; a different column starts at asc.
            const current = state.sort;
            const sort =
                current?.column !== action.column
                    ? ({ column: action.column, direction: 'asc' } as const)
                    : current.direction === 'asc'
                      ? ({ column: action.column, direction: 'desc' } as const)
                      : null;
            return { ...state, sort, page: 1 };
        }
        case 'SET_COLUMN_FILTER': {
            const value = action.value.trim();
            if (state.columnFilters[action.column] === value) return state;
            return { ...state, columnFilters: { ...state.columnFilters, [action.column]: value }, page: 1 };
        }
        case 'CLEAR_COLUMN_FILTERS':
            if (REVENUE_ANALYTICS_FILTER_COLUMNS.every((column) => state.columnFilters[column] === '')) return state;
            return { ...state, columnFilters: EMPTY_REVENUE_ANALYTICS_COLUMN_FILTERS, page: 1 };
        case 'TOGGLE_COLUMN_VISIBILITY': {
            const column = findRevenueAnalyticsColumn(action.column);
            if (!column.hideable) return state;
            const visible = !state.columnVisibility[column.key];
            const next = { ...state, columnVisibility: { ...state.columnVisibility, [column.key]: visible } };
            if (visible) return next;
            // A hidden column stops influencing the result set: its sort and
            // filter go with it, which can change the page count.
            const dropsSort = column.sortColumn !== undefined && state.sort?.column === column.sortColumn;
            const dropsFilter = column.filterColumn !== undefined && state.columnFilters[column.filterColumn] !== '';
            if (!dropsSort && !dropsFilter) return next;
            return {
                ...next,
                sort: dropsSort ? null : state.sort,
                columnFilters:
                    dropsFilter && column.filterColumn
                        ? { ...state.columnFilters, [column.filterColumn]: '' }
                        : state.columnFilters,
                page: 1,
            };
        }
        case 'RESET_COLUMN_VISIBILITY':
            if (isEveryRevenueAnalyticsColumnVisible(state.columnVisibility)) return state;
            return { ...state, columnVisibility: DEFAULT_REVENUE_ANALYTICS_COLUMN_VISIBILITY };
        case 'TOGGLE_SYMBOLS_COLUMN_VISIBILITY': {
            const column = findAggregatedSymbolsColumn(action.column);
            if (!column.hideable) return state;
            const visible = !state.symbolsColumnVisibility[column.key];
            const next = {
                ...state,
                symbolsColumnVisibility: { ...state.symbolsColumnVisibility, [column.key]: visible },
            };
            if (visible) return next;
            // Like the providers registry: a hidden column's sort and filter
            // go with it. Both are shared across the tabs, so the drop shows
            // on the providers tab too, the same as clearing a filter there.
            const dropsSort = column.sortColumn !== undefined && state.sort?.column === column.sortColumn;
            const dropsFilter = column.filterColumn !== undefined && state.columnFilters[column.filterColumn] !== '';
            if (!dropsSort && !dropsFilter) return next;
            return {
                ...next,
                sort: dropsSort ? null : state.sort,
                columnFilters:
                    dropsFilter && column.filterColumn
                        ? { ...state.columnFilters, [column.filterColumn]: '' }
                        : state.columnFilters,
                page: 1,
            };
        }
        case 'RESET_SYMBOLS_COLUMN_VISIBILITY':
            if (isEveryAggregatedSymbolsColumnVisible(state.symbolsColumnVisibility)) return state;
            return { ...state, symbolsColumnVisibility: DEFAULT_AGGREGATED_SYMBOLS_COLUMN_VISIBILITY };
        case 'SET_SYMBOL_SEARCH_INPUT':
            return { ...state, symbolSearchInput: action.value };
        case 'APPLY_SYMBOL_SEARCH': {
            // Normalize the input to the committed (trimmed) term so a
            // whitespace-only entry doesn't leave stale text in the box.
            const applied = state.symbolSearchInput.trim();
            if (applied === state.appliedSymbolSearch && applied === state.symbolSearchInput) return state;
            return { ...state, symbolSearchInput: applied, appliedSymbolSearch: applied, page: 1 };
        }
        case 'CLEAR_SYMBOL_SEARCH':
            if (state.symbolSearchInput === '' && state.appliedSymbolSearch === '') return state;
            return { ...state, symbolSearchInput: '', appliedSymbolSearch: '', page: 1 };
        case 'START_CSV_DOWNLOAD':
            if (state.isDownloadingCsv) return state;
            return { ...state, isDownloadingCsv: true, csvDownloadFailed: false };
        case 'FAIL_CSV_DOWNLOAD':
            // Also clear the in-flight flag so the spinner and the error can never coexist.
            if (state.csvDownloadFailed && !state.isDownloadingCsv) return state;
            return { ...state, csvDownloadFailed: true, isDownloadingCsv: false };
        case 'END_CSV_DOWNLOAD':
            if (!state.isDownloadingCsv) return state;
            return { ...state, isDownloadingCsv: false };
    }
}
